/**
 * Exercise template editor — create or edit a single library exercise.
 *
 * Renders the form from the K-OS Health design's "New exercise" screen:
 *
 *   • Type      strength / cardio steady / cardio intervals / mobility / plyometric / hold
 *   • Name      free text
 *   • Default block   warmup / main / accessory / cooldown
 *   • Default parameters  type-specific (sets/reps, distance/pace, intervals/work/rest, …)
 *   • Prescription label
 *   • Cues       one-per-line
 *   • Reference URL  → ReferenceButton auto-detects video / PDF / image / page
 *
 * The "Default parameters" block is stored on the exercise as `defaults: Record<string, unknown>`.
 * The shape is type-dependent and is read back when this editor opens an existing exercise.
 */

import { useState, type ReactNode } from 'react';
import { Icon } from '@k-os/ui';
import {
  useCreateExercise,
  usePatchExercise,
  useEquipment,
  type ExerciseDto,
  type ExerciseType,
  type ExerciseBlock,
} from '../../../api/health';
import { EXERCISE_BLOCK_META, EXERCISE_TYPE_META } from './atoms';
import { MUSCLE_GROUPS } from '../exercises/atoms';
import styles from './workouts.module.css';

const TYPE_KEYS = Object.keys(EXERCISE_TYPE_META) as ExerciseType[];
const BLOCK_KEYS = Object.keys(EXERCISE_BLOCK_META) as ExerciseBlock[];

type Level = 'beginner' | 'intermediate' | 'advanced';
const LEVELS: Array<{ v: Level; label: string }> = [
  { v: 'beginner', label: 'Beginner' },
  { v: 'intermediate', label: 'Intermediate' },
  { v: 'advanced', label: 'Advanced' },
];

const TYPE_DESC: Record<ExerciseType, string> = {
  strength: 'Weighted resistance · tracks weight, reps, difficulty',
  cardio_steady: 'Steady-state aerobic · tracks distance, pace, duration',
  cardio_intervals: 'Repeated efforts · tracks intervals × duration × intensity',
  mobility: 'Range of motion · tracks duration or reps',
  plyometric: 'Explosive · tracks sets × reps',
  hold: 'Time under tension · tracks duration',
};

const TYPE_DEFAULTS: Record<ExerciseType, Record<string, unknown>> = {
  strength: { sets: 3, reps: 8, load: 70, rest_s: 90 },
  cardio_steady: { distance_km: 5, pace: '5:30', duration_min: 30, zone: 'Z2' },
  cardio_intervals: { intervals: 6, work_s: 60, rest_s: 60, effort: '5K pace' },
  mobility: { duration_s: 60, reps: 8, sides: true },
  plyometric: { sets: 3, reps: 8, rest_s: 60 },
  hold: { sets: 3, duration_s: 45 },
};

export function ExerciseEditor({
  exercise,
  defaultBlock,
  onCancel,
  onSaved,
  onArchive,
  crumbLabel = 'Workouts',
}: {
  exercise?: ExerciseDto;
  defaultBlock?: ExerciseBlock;
  onCancel: () => void;
  onSaved: (saved: ExerciseDto) => void;
  /** Optional archive action (edit mode only). When provided, an Archive
   *  button is shown in the footer. */
  onArchive?: ((exercise: ExerciseDto) => void) | undefined;
  /** Label for the first breadcrumb crumb. Defaults to 'Workouts'. */
  crumbLabel?: string;
}) {
  const isEdit = !!exercise;
  const create = useCreateExercise();
  const patch = usePatchExercise();
  const equipmentQ = useEquipment();

  const [type, setType] = useState<ExerciseType>(exercise?.type ?? 'strength');
  const [name, setName] = useState(exercise?.name ?? '');
  const [block, setBlock] = useState<ExerciseBlock>(
    exercise?.defaultBlock ?? defaultBlock ?? 'main',
  );
  const [prescription, setPrescription] = useState(exercise?.prescription ?? '');
  const [referenceUrl, setReferenceUrl] = useState(exercise?.referenceUrl ?? '');
  const [cues, setCues] = useState<string[]>(
    exercise?.cues && exercise.cues.length > 0 ? exercise.cues : [''],
  );
  const [defaults, setDefaults] = useState<Record<string, unknown>>(
    (exercise?.defaults as Record<string, unknown>) ?? TYPE_DEFAULTS[exercise?.type ?? 'strength'],
  );

  // New catalog fields (Exercises page).
  const [primaryMuscle, setPrimaryMuscle] = useState<string | null>(
    exercise?.primaryMuscle ?? null,
  );
  const [targetMuscles, setTargetMuscles] = useState<string[]>(exercise?.targetMuscles ?? []);
  const [equipmentId, setEquipmentId] = useState<string | null>(exercise?.equipmentId ?? null);
  const [level, setLevel] = useState<Level | null>(exercise?.level ?? null);
  const [coverImageUrl, setCoverImageUrl] = useState(exercise?.coverImageUrl ?? '');
  const [frontImageUrl, setFrontImageUrl] = useState(exercise?.frontImageUrl ?? '');
  const [backImageUrl, setBackImageUrl] = useState(exercise?.backImageUrl ?? '');

  function onChangeType(t: ExerciseType): void {
    setType(t);
    setDefaults(TYPE_DEFAULTS[t]);
  }

  function toggleChip(list: string[], setList: (v: string[]) => void, value: string): void {
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  }

  function updateCue(i: number, v: string): void {
    setCues((prev) => {
      const next = [...prev];
      next[i] = v;
      // Auto-append a blank line so the user always has somewhere to type.
      if (i === next.length - 1 && v.trim() !== '') next.push('');
      return next;
    });
  }
  function removeCue(i: number): void {
    setCues((prev) => prev.filter((_, idx) => idx !== i));
  }

  const ready = name.trim().length > 0 && !!type && !!block;

  async function save(): Promise<void> {
    const payload = {
      name: name.trim(),
      type,
      defaultBlock: block,
      cues: cues.map((c) => c.trim()).filter(Boolean),
      prescription: prescription.trim() || null,
      referenceUrl: referenceUrl.trim() || null,
      defaults,
      primaryMuscle: primaryMuscle ?? null,
      targetMuscles,
      equipmentId: equipmentId ?? null,
      level: level ?? null,
      coverImageUrl: coverImageUrl.trim() || null,
      frontImageUrl: frontImageUrl.trim() || null,
      backImageUrl: backImageUrl.trim() || null,
    };
    if (isEdit && exercise) {
      const res = await patch.mutateAsync({ id: exercise.id, patch: payload });
      onSaved(res.exercise);
    } else {
      const res = await create.mutateAsync(payload);
      onSaved(res.exercise);
    }
  }

  return (
    <div className={styles.editor}>
      <div className={styles.crumb}>
        <span className={styles.crumbLink} onClick={onCancel}>
          {crumbLabel}
        </span>
        <Icon name="chevron" size={11} />
        <span className={styles.crumbActive}>{isEdit ? 'Edit exercise' : 'New exercise'}</span>
      </div>
      <h2 className={styles.editorTitle}>{isEdit ? 'Edit exercise' : 'New exercise'}</h2>
      <div className={styles.editorIntro}>
        Exercises are the building blocks. Type determines what's tracked — strength logs weight &
        reps; cardio logs distance & pace; mobility logs duration or reps.
      </div>

      {/* Type */}
      <FieldLabel>Type</FieldLabel>
      <div className={styles.editorTypeGrid}>
        {TYPE_KEYS.map((t) => (
          <button
            key={t}
            type="button"
            className={`${styles.editorTypeCard} ${type === t ? styles.editorTypeCardActive : ''}`}
            onClick={() => onChangeType(t)}
          >
            <div className={styles.editorTypeCardHead}>
              <span className={styles.editorTypeDot} style={{ background: EXERCISE_TYPE_META[t].color }} />
              <span className={styles.editorTypeName}>{EXERCISE_TYPE_META[t].name}</span>
            </div>
            <div className={styles.editorTypeDesc}>{TYPE_DESC[t]}</div>
          </button>
        ))}
      </div>

      {/* Name */}
      <FieldLabel>Name</FieldLabel>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Back squat · Easy 5km · Hip CARs"
        className={`${styles.editorInput} ${styles.editorInputLg}`}
      />

      {/* Default block */}
      <FieldLabel hint="default block when added to a workout">Default block</FieldLabel>
      <div className={styles.editorBlockGrid}>
        {BLOCK_KEYS.map((b) => (
          <button
            key={b}
            type="button"
            className={`${styles.editorBlockCard} ${block === b ? styles.editorBlockCardActive : ''}`}
            onClick={() => setBlock(b)}
          >
            <div className={styles.editorBlockName}>{EXERCISE_BLOCK_META[b].name}</div>
            <div className={styles.editorBlockDesc}>{EXERCISE_BLOCK_META[b].desc}</div>
          </button>
        ))}
      </div>

      {/* Default parameters */}
      <FieldLabel hint={`fields for ${EXERCISE_TYPE_META[type].name.toLowerCase()}`}>
        Default parameters
      </FieldLabel>
      <ParamFields type={type} value={defaults} onChange={setDefaults} />

      {/* Prescription label */}
      <FieldLabel optional hint="short form like '4×6 @ 70%' or '5km Z2'">
        Prescription label
      </FieldLabel>
      <input
        value={prescription}
        onChange={(e) => setPrescription(e.target.value)}
        placeholder="e.g. 4×6 @ 70%  ·  5km Z2  ·  3×45s"
        className={styles.editorInput}
      />

      {/* Primary muscle */}
      <FieldLabel optional hint="main muscle worked — shown on the exercise card">
        Primary muscle
      </FieldLabel>
      <select
        value={primaryMuscle ?? ''}
        onChange={(e) => setPrimaryMuscle(e.target.value || null)}
        className={styles.editorInput}
      >
        <option value="">None</option>
        {MUSCLE_GROUPS.map((m) => (
          <option key={m.slug} value={m.slug}>
            {m.label}
          </option>
        ))}
      </select>

      {/* Target muscles */}
      <FieldLabel optional hint="all muscle groups this exercise targets">
        Target muscles
      </FieldLabel>
      <div className={styles.editorChipRow}>
        {MUSCLE_GROUPS.map((m) => (
          <button
            key={m.slug}
            type="button"
            className={`${styles.filterChip} ${targetMuscles.includes(m.slug) ? styles.filterChipActive : ''}`}
            onClick={() => toggleChip(targetMuscles, setTargetMuscles, m.slug)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Equipment (catalog) */}
      <FieldLabel optional>Equipment</FieldLabel>
      <select
        value={equipmentId ?? ''}
        onChange={(e) => setEquipmentId(e.target.value || null)}
        className={styles.editorInput}
      >
        <option value="">None</option>
        {(equipmentQ.data ?? []).map((eq) => (
          <option key={eq.id} value={eq.id}>
            {eq.name}
          </option>
        ))}
      </select>

      {/* Level */}
      <FieldLabel optional>Level</FieldLabel>
      <div className={styles.editorChipRow}>
        {LEVELS.map((l) => (
          <button
            key={l.v}
            type="button"
            className={`${styles.filterChip} ${level === l.v ? styles.filterChipActive : ''}`}
            onClick={() => setLevel(level === l.v ? null : l.v)}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* Images */}
      <FieldLabel optional hint="image URLs for the cover / front / back figures">
        Images
      </FieldLabel>
      <div className={styles.editorParamGrid}>
        <ParamField label="Cover image URL" optional>
          <input
            value={coverImageUrl}
            onChange={(e) => setCoverImageUrl(e.target.value)}
            placeholder="https://…"
            className={styles.editorInput}
          />
        </ParamField>
        <ParamField label="Front image URL" optional>
          <input
            value={frontImageUrl}
            onChange={(e) => setFrontImageUrl(e.target.value)}
            placeholder="https://…"
            className={styles.editorInput}
          />
        </ParamField>
        <ParamField label="Back image URL" optional>
          <input
            value={backImageUrl}
            onChange={(e) => setBackImageUrl(e.target.value)}
            placeholder="https://…"
            className={styles.editorInput}
          />
        </ParamField>
      </div>

      {/* Cues */}
      <FieldLabel optional hint="how-to-perform steps · one per line">Cues</FieldLabel>
      <div className={styles.editorCues}>
        {cues.map((c, i) => (
          <div key={i} className={styles.editorCueRow}>
            <span className={styles.editorCueBullet}>·</span>
            <input
              value={c}
              onChange={(e) => updateCue(i, e.target.value)}
              placeholder="Add a cue…"
              className={styles.editorInputFlat}
            />
            {c.trim() && (
              <button
                type="button"
                className={styles.editorCueRemove}
                onClick={() => removeCue(i)}
                title="Remove cue"
              >
                <Icon name="x" size={11} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Reference link */}
      <FieldLabel optional hint="image, page, PDF, or video — shown as a quick-open button on the exercise">
        Reference link
      </FieldLabel>
      <input
        value={referenceUrl}
        onChange={(e) => setReferenceUrl(e.target.value)}
        placeholder="https://… (YouTube, PDF, image, doc)"
        className={styles.editorInput}
      />

      {/* Footer */}
      <div className={styles.editorFooter}>
        <button type="button" className="kos-btn" onClick={onCancel}>
          Cancel
        </button>
        {isEdit && exercise && onArchive && (
          <button
            type="button"
            className="kos-btn"
            style={{ color: 'var(--neg)' }}
            onClick={() => onArchive(exercise)}
          >
            <Icon name="x" size={11} /> Archive
          </button>
        )}
        <span className={styles.editorFooterRight}>
          <span className={styles.editorReady}>
            {ready ? (
              <span style={{ color: 'var(--accent)' }}>● Ready to save</span>
            ) : (
              <span style={{ color: 'var(--ink-4)' }}>○ Name + type + block required</span>
            )}
          </span>
          <button
            type="button"
            className="kos-btn kos-btn-primary"
            disabled={!ready || create.isPending || patch.isPending}
            onClick={() => void save()}
          >
            <Icon name="check" size={11} />{' '}
            {isEdit ? 'Save changes' : 'Create exercise'}
          </button>
        </span>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function FieldLabel({
  children,
  optional,
  hint,
}: {
  children: ReactNode;
  optional?: boolean | undefined;
  hint?: string | undefined;
}) {
  return (
    <div className={styles.editorLabel}>
      <span className={styles.editorLabelText}>{children}</span>
      {optional && <span className={styles.editorLabelOptional}>OPTIONAL</span>}
      {hint && <span className={styles.editorLabelHint}>{hint}</span>}
    </div>
  );
}

function ParamFields({
  type,
  value,
  onChange,
}: {
  type: ExerciseType;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
}) {
  const setKey = (k: string, v: unknown): void => onChange({ ...value, [k]: v });
  const num = (k: string): number | '' => (typeof value[k] === 'number' ? (value[k] as number) : '');

  if (type === 'strength' || type === 'plyometric') {
    return (
      <div className={styles.editorParamGrid}>
        <ParamField label="Sets">
          <input
            type="number"
            min={1}
            className={styles.editorInput}
            value={num('sets')}
            onChange={(e) => setKey('sets', Number(e.target.value))}
          />
        </ParamField>
        <ParamField label="Target reps">
          <input
            type="number"
            min={1}
            className={styles.editorInput}
            value={num('reps')}
            onChange={(e) => setKey('reps', Number(e.target.value))}
          />
        </ParamField>
        {type === 'strength' && (
          <ParamField label="Load (% of 1RM or kg)" optional>
            <input
              className={styles.editorInput}
              value={(value['load'] as string | number | undefined) ?? ''}
              onChange={(e) => setKey('load', e.target.value)}
              placeholder="70 or 70%"
            />
          </ParamField>
        )}
        <ParamField label="Rest (seconds)" optional>
          <input
            type="number"
            min={0}
            className={styles.editorInput}
            value={num('rest_s')}
            onChange={(e) => setKey('rest_s', Number(e.target.value))}
          />
        </ParamField>
      </div>
    );
  }

  if (type === 'cardio_steady') {
    return (
      <div className={styles.editorParamGrid}>
        <ParamField label="Distance (km)">
          <input
            type="number"
            step="0.1"
            min={0}
            className={styles.editorInput}
            value={num('distance_km')}
            onChange={(e) => setKey('distance_km', Number(e.target.value))}
          />
        </ParamField>
        <ParamField label="Target pace">
          <input
            className={styles.editorInput}
            value={(value['pace'] as string | undefined) ?? ''}
            onChange={(e) => setKey('pace', e.target.value)}
            placeholder="5:30/km"
          />
        </ParamField>
        <ParamField label="Duration (min)" optional>
          <input
            type="number"
            min={0}
            className={styles.editorInput}
            value={num('duration_min')}
            onChange={(e) => setKey('duration_min', Number(e.target.value))}
          />
        </ParamField>
        <ParamField label="Zone" optional>
          <input
            className={styles.editorInput}
            value={(value['zone'] as string | undefined) ?? ''}
            onChange={(e) => setKey('zone', e.target.value)}
            placeholder="Z2"
          />
        </ParamField>
      </div>
    );
  }

  if (type === 'cardio_intervals') {
    return (
      <div className={styles.editorParamGrid}>
        <ParamField label="Intervals">
          <input
            type="number"
            min={1}
            className={styles.editorInput}
            value={num('intervals')}
            onChange={(e) => setKey('intervals', Number(e.target.value))}
          />
        </ParamField>
        <ParamField label="Effort target">
          <input
            className={styles.editorInput}
            value={(value['effort'] as string | undefined) ?? ''}
            onChange={(e) => setKey('effort', e.target.value)}
            placeholder="5K pace"
          />
        </ParamField>
        <ParamField label="Work duration (s)">
          <input
            type="number"
            min={0}
            className={styles.editorInput}
            value={num('work_s')}
            onChange={(e) => setKey('work_s', Number(e.target.value))}
          />
        </ParamField>
        <ParamField label="Rest duration (s)">
          <input
            type="number"
            min={0}
            className={styles.editorInput}
            value={num('rest_s')}
            onChange={(e) => setKey('rest_s', Number(e.target.value))}
          />
        </ParamField>
      </div>
    );
  }

  if (type === 'mobility') {
    return (
      <div className={styles.editorParamGrid}>
        <ParamField label="Reps" optional>
          <input
            type="number"
            min={0}
            className={styles.editorInput}
            value={num('reps')}
            onChange={(e) => setKey('reps', Number(e.target.value))}
          />
        </ParamField>
        <ParamField label="Duration (seconds)" optional>
          <input
            type="number"
            min={0}
            className={styles.editorInput}
            value={num('duration_s')}
            onChange={(e) => setKey('duration_s', Number(e.target.value))}
          />
        </ParamField>
        <label className={styles.editorParamCheck}>
          <input
            type="checkbox"
            checked={!!value['sides']}
            onChange={(e) => setKey('sides', e.target.checked)}
          />
          Perform on both sides
        </label>
      </div>
    );
  }

  // hold
  return (
    <div className={styles.editorParamGrid}>
      <ParamField label="Sets">
        <input
          type="number"
          min={1}
          className={styles.editorInput}
          value={num('sets')}
          onChange={(e) => setKey('sets', Number(e.target.value))}
        />
      </ParamField>
      <ParamField label="Hold duration (seconds)">
        <input
          type="number"
          min={0}
          className={styles.editorInput}
          value={num('duration_s')}
          onChange={(e) => setKey('duration_s', Number(e.target.value))}
        />
      </ParamField>
    </div>
  );
}

function ParamField({
  label,
  optional,
  children,
}: {
  label: string;
  optional?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <div className={styles.editorParamField}>
      <FieldLabel optional={optional}>{label}</FieldLabel>
      {children}
    </div>
  );
}
